import { getHospitalityMenuCategory, updateHospitalityMenuCategoryOrders } from "@/sideEffects/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { HospitalityMenuDetails } from "@/lib/types";
import { GripIcon } from "lucide-react";
// @ts-ignore
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import { cn } from "@/lib/utils";
import { HospitalityMenuCategory } from "./HospitalityMenuCategory";
import { showApiError, showSuccess } from "@/lib/toast";
import { LoadingOverlay } from "@/components/LoadingOverlay";
import { HospitalityMenuCategoryItems } from "./items/HospitalityMenuCategoryItems";
import { LoadingSmall } from "@/components/LoadingSmall";
import { useTranslation } from "react-i18next";

type Props = {
  hospitalityMenu: HospitalityMenuDetails;
};

const reorder = <T,>(list: T[], startIndex: number, endIndex: number): T[] => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

type OrderPayloadItem = {
  hospitalityMenuId: number;
  categoryId: number;
  order: number;
};

export const HospitalityMenuCategories: React.FC<Props> = ({ hospitalityMenu }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: hospitalityMenuCategories, isLoading: isHospitalityMenuCategoriesLoading } = useQuery({
    queryKey: ["getHospitalityMenuCategory", hospitalityMenu.id],
    queryFn: () => getHospitalityMenuCategory(hospitalityMenu.id),
  });

  const hospitalityMenuCategoriesData = hospitalityMenuCategories?.data;

  const updateHospitalityMenuCategoryOrdersMutation = useMutation({
    mutationFn: (values: OrderPayloadItem[]) => updateHospitalityMenuCategoryOrders(values),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getHospitalityMenuCategory", hospitalityMenu.id],
      });
      showSuccess(t("common.updatedSuccess"));
    },
    onError: showApiError,
  });

  if (!hospitalityMenuCategoriesData || isHospitalityMenuCategoriesLoading) {
    return <LoadingSmall />;
  }

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    const items = reorder(hospitalityMenuCategoriesData, result.source.index, result.destination.index);

    queryClient.setQueryData(["getHospitalityMenuCategory", hospitalityMenu.id], {
      ...hospitalityMenuCategories,
      data: items,
    });

    const ordersPayload = items.map(
      (item, index) =>
        ({
          hospitalityMenuId: hospitalityMenu.id,
          categoryId: item.id,
          order: index + 1,
        } satisfies OrderPayloadItem)
    );

    updateHospitalityMenuCategoryOrdersMutation.mutate(ordersPayload);
  };

  if (hospitalityMenuCategoriesData?.length === 0) {
    return <div className="text-sm">{t("hospitalityMenu.noCategories")}</div>;
  }

  return (
    <LoadingOverlay
      isLoading={updateHospitalityMenuCategoryOrdersMutation.isPending || isHospitalityMenuCategoriesLoading}
    >
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable-categories">
          {/* @ts-ignore */}
          {(provided, snapshot) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className={cn("w-full p-2", {
                "bg-muted/10": snapshot.isDraggingOver,
              })}
            >
              {hospitalityMenuCategoriesData.map((category, index) => (
                <Draggable key={category.id} draggableId={category.id.toString()} index={index}>
                  {/* @ts-ignore */}
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className={cn("mb-4", {
                        "bg-muted/50": snapshot.isDragging,
                      })}
                      style={provided.draggableProps.style}
                    >
                      <div className="border p-2 w-full flex items-center gap-2">
                        <span {...provided.dragHandleProps} className="cursor-grab">
                          <GripIcon className="w-4 h-4" />
                        </span>
                        <HospitalityMenuCategory category={category} />
                      </div>
                      <HospitalityMenuCategoryItems category={category} />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </LoadingOverlay>
  );
};
